import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  EmailMessageAttachmentDTO,
  EmailMessageDTO,
  EmailMessageThreadDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useEmailMessageApi } from '../../hooks/useEmailMessageApi';
import { useFileApi } from '../../hooks/useFileApi';
import ImageLink from '../common/ImageLink';
import FileUpload from '../common/FileUpload';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import EmailMessageItem from '../common/formElements/EmailMessageItem';
import EmailMessageItemHeader from '../common/formElements/EmailMessageItemHeader';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface EmailMessageThreadFormProps {
  initialValues: EmailMessageThreadDTO;
  onSubmit: (data: EmailMessageThreadDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  details: {
    id: 'details',
    title: 'Details',
  },
  reply: {
    id: 'reply',
    title: 'Reply',
  },
  pastEmails: {
    id: 'past-emails',
    title: 'Past Emails',
  },
} as const;

// Extracted Info Card Component
const InfoCard: React.FC<{ label: string; value: string; className?: string }> = ({
  label,
  value,
  className = 'bg-gray-50 dark:bg-gray-800',
}) => (
  <div className={`${className} p-4 rounded-lg`}>
    <label className="text-sm font-medium text-gray-500 dark:text-gray-400 block mb-1">
      {label}
    </label>
    <span className="text-base text-gray-900 dark:text-gray-100">{value}</span>
  </div>
);

// Extracted User Card Component
const UserCard: React.FC<{
  label: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  colorScheme: 'blue' | 'purple';
}> = ({ label, firstName, lastName, email, colorScheme }) => {
  const colors = {
    blue: {
      container: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
      label: 'text-blue-700 dark:text-blue-300',
      avatar: 'bg-blue-500',
    },
    purple: {
      container: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
      label: 'text-purple-700 dark:text-purple-300',
      avatar: 'bg-purple-500',
    },
  };

  const scheme = colors[colorScheme];

  return (
    <div className={`${scheme.container} p-4 rounded-lg border`}>
      <label className={`text-sm font-medium ${scheme.label} block mb-2`}>{label}</label>
      <div className="flex items-center gap-2">
        <div
          className={`w-10 h-10 rounded-full ${scheme.avatar} text-white flex items-center justify-center font-semibold`}
        >
          {firstName?.[0]}
          {lastName?.[0]}
        </div>
        <div>
          <div className="text-base text-gray-900 dark:text-gray-100 font-medium">
            {firstName} {lastName}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{email}</div>
        </div>
      </div>
    </div>
  );
};

const EmailMessageThreadForm: React.FC<EmailMessageThreadFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<EmailMessageThreadDTO>(initialValues);
  const [replyData, setReplyData] = useState<EmailMessageDTO>({});
  const [emailMessages, setEmailMessages] = useState<EmailMessageDTO[]>([]);
  const [attachments, setAttachments] = useState<EmailMessageAttachmentDTO[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);

  const { uploadFile } = useFileApi();
  const { search: searchEmailMessages } = useEmailMessageApi();

  const validationRules: ValidationRules = {
    message: [{ required: true, message: 'Message is required' }],
  };

  const { errors, validate } = useFormValidation<EmailMessageDTO>(validationRules);

  useEffect(() => {
    const fetchEmailMessages = async () => {
      try {
        const response = await searchEmailMessages({
          filters: {
            emailMessageThreadId: formData.id,
          },
          page: 1,
          pageSize: 9999,
          sortBy: 'CreatedUtc',
          sortDescending: true,
        });
        setEmailMessages(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching email thread: ', error);
      }
    };

    fetchEmailMessages();

    setReplyData({
      emailMessageThreadId: formData.id,
      subject: formData.subject ? `Re: ${formData.subject}` : undefined,
      reference: formData.reference,
      memberId: formData.rootEmailMessage?.memberId,
      adminUserId: formData.rootEmailMessage?.adminUserId,
      isReply: true,
      isReplyFromAdmin: true,
      parentEmailMessageId: formData.lastEmailMessageId,
      emailAttachments: [],
    });
  }, [formData.id]);

  const handleSave = () => {
    const dataToSubmit = {
      ...replyData,
      emailAttachments: attachments,
    };

    if (validate(dataToSubmit)) {
      onSubmit(dataToSubmit);
    }
  };

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingFiles(true);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Upload file to blob storage
        const uploadResponse = await uploadFile('files', file.name, file, 'email-attachments');

        if (uploadResponse && uploadResponse.success && uploadResponse.data) {
          // Create EmailMessageAttachment DTO
          const attachment: EmailMessageAttachmentDTO = {
            id: uploadResponse.data, // This should be the FileRecordId returned from upload
            fileRecordId: uploadResponse.data,
            emailMessageId: undefined, // Will be set when email is created
            createdUtc: new Date(),
          };

          return attachment;
        }
        return null;
      });

      const uploadedAttachments = await Promise.all(uploadPromises);
      const validAttachments = uploadedAttachments.filter(
        (a): a is EmailMessageAttachmentDTO => a !== null,
      );

      // Add new attachments to existing ones
      setAttachments((prev) => [...prev, ...validAttachments]);
    } catch (error) {
      console.error('Error uploading attachments:', error);
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleRemoveAttachment = (fileRecordId: string) => {
    setAttachments((prev) => prev.filter((a) => a.fileRecordId !== fileRecordId));
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading || uploadingFiles}
    >
      <FormSection id={FORM_SECTIONS.details.id} title={FORM_SECTIONS.details.title} columns={1}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Thread Info */}
          <div className="space-y-4">
            <InfoCard
              label="Created Date"
              value={convertToUserTime(formData.createdUtc?.toString() ?? '', true)}
            />
            <InfoCard label="Component" value={formData.component?.title || 'N/A'} />
            <InfoCard
              label="Reference"
              value={formData.reference || 'N/A'}
              className="bg-gray-50 dark:bg-gray-800"
            />
          </div>

          {/* Right Column - User Info */}
          <div className="space-y-4">
            {formData.rootEmailMessage?.member && (
              <UserCard
                label="Member"
                firstName={formData.rootEmailMessage.member.firstName}
                lastName={formData.rootEmailMessage.member.lastName}
                email={formData.rootEmailMessage.member.email}
                colorScheme="blue"
              />
            )}

            {formData.rootEmailMessage?.adminUser && (
              <UserCard
                label="Admin User"
                firstName={formData.rootEmailMessage.adminUser.firstName}
                lastName={formData.rootEmailMessage.adminUser.lastName}
                email={formData.rootEmailMessage.adminUser.email}
                colorScheme="purple"
              />
            )}
          </div>
        </div>
      </FormSection>

      <FormSection id={FORM_SECTIONS.reply.id} title={FORM_SECTIONS.reply.title} columns={1}>
        <FormField
          id="message"
          label="Message"
          type="richtext"
          value={replyData.message || ''}
          onChange={(e) => handleChange(e, setReplyData)}
          placeholder="Enter Message"
          required={true}
          error={errors.message}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            id="emailCC"
            label="CC"
            type="text"
            value={replyData.emailCC || ''}
            onChange={(e) => handleChange(e, setReplyData)}
            placeholder="email1@example.com, email2@example.com"
            required={false}
            error={errors.emailCC}
          />
          <FormField
            id="emailBCC"
            label="BCC"
            type="text"
            value={replyData.emailBCC || ''}
            onChange={(e) => handleChange(e, setReplyData)}
            placeholder="email1@example.com, email2@example.com"
            required={false}
            error={errors.emailBCC}
          />
        </div>

        {/* Display uploaded attachments */}
        {attachments.length > 0 && (
          <div className="space-y-2">
            <div className="space-y-2">
              {attachments.map((attachment) => (
                <div
                  key={attachment.fileRecordId}
                  className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-3 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <ImageLink fileId={attachment.fileRecordId ?? ''} label="" />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(attachment.fileRecordId ?? '')}
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <FileUpload
          id="emailAttachments"
          onChange={handleAttachmentUpload}
          title="Add Email Attachments"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={false}
          allowMultiple={true}
          isUploading={uploadingFiles}
        />
      </FormSection>

      {emailMessages.map((email) => (
        <FormSection
          key={email.id}
          id={FORM_SECTIONS.pastEmails.id}
          title={<EmailMessageItemHeader data={email}/>}
          columns={1}
        >
          <EmailMessageItem data={email} />
        </FormSection>
      ))}
    </Form>
  );
};

export default EmailMessageThreadForm;
